<template>
  <div class="app">
    <h1 class="title">{{ $t('generate.title') }}</h1>
    <div class="controls">
      <label>
        {{ $t('generate.difficulty') }}:
        <select v-model="difficulty">
          <option value="easy">{{ $t('generate.difficulties.easy') }}</option>
          <option value="medium">{{ $t('generate.difficulties.medium') }}</option>
          <option value="hard">{{ $t('generate.difficulties.hard') }}</option>
        </select>
      </label>

      <div class="themes-group">
        <span class="theme-label">{{ $t('generate.themes.label') }}:</span>
        <div class="theme-options">
          <label class="theme-checkbox">
            <input type="checkbox" v-model="themes" value="space">
            {{ $t('generate.themes.space') }}
          </label>
          <label class="theme-checkbox">
            <input type="checkbox" v-model="themes" value="fantasy">
            {{ $t('generate.themes.fantasy') }}
          </label>
          <label class="theme-checkbox">
            <input type="checkbox" v-model="themes" value="animals">
            {{ $t('generate.themes.animals') }}
          </label>
        </div>
      </div>

      <button @click="generatePreviews">{{ $t('generate.regenerateButton') }}</button>
      <button @click="downloadPdf">{{ $t('generate.downloadButton') }}</button>
    </div>

    <div class="preview-container">
      <img v-if="previewUrl" :src="previewUrl" class="preview-image" />
      <div v-else class="loading">Loading previews...</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  sessionId: String
})

const router = useRouter()
const { locale } = useI18n()
const difficulty = ref('medium')
const previewUrl = ref('')
const sessionId = ref(props.sessionId)
const themes = ref(['fantasy', 'space', 'animals']) // Default to all themes

// Ensure at least one theme is always selected
watch(themes, (newThemes) => {
  if (newThemes.length === 0) {
    themes.value = ['fantasy', 'space', 'animals']
  }
})

const apiUrl = import.meta.env.VITE_API_URL;

onMounted(async () => {
  if (!sessionId.value) {
    const response = await axios.get(`${apiUrl}/api/get-new-guid`)
    const guid = response.data
    await router.replace(`/generate/${guid}`)
    sessionId.value = guid
    await generatePreviews()
    return
  }
  generatePreviews()
})

const generatePreviews = async () => {
  previewUrl.value = ''
  const response = await axios.get(`${apiUrl}/api/preview-maze/${sessionId.value}`, {
    params: { 
      difficulty: difficulty.value,
      language: locale.value,
      themes: themes.value.join(',')
    },
    responseType: 'blob'
  })
  const reader = new FileReader()
  reader.onload = () => {
    previewUrl.value = reader.result
  }
  reader.readAsDataURL(response.data)
}

const downloadPdf = async () => {
  const response = await axios.get(`${apiUrl}/api/generate-maze-pdf/${sessionId.value}`, {
    params: { 
      difficulty: difficulty.value,
      language: locale.value,
      themes: themes.value.join(',')
    },
    responseType: 'blob',
  })

  const blob = new Blob([response.data], { type: 'application/pdf' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = 'mazes.pdf'
  link.click()
  URL.revokeObjectURL(url)
}
</script>

<style scoped>
.app {
  padding: 2rem;
  font-family: sans-serif;
  text-align: center;
}

.title {
  margin-bottom: 1rem;
}

.controls {
  margin-bottom: 1.5rem;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
  justify-content: center;
}

.controls label {
  font-weight: bold;
}

button {
  background-color: #00cc44;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  font-weight: bold;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

button:hover {
  background-color: #00b33c;
}

.preview-container {
  max-width: 800px;  /* Set max-width to match the preview image size */
  overflow-x: hidden;  /* Hide overflow since we're controlling the size now */
  margin: 0 auto;
  padding: 1rem;
}

.preview-image {
  width: 100%;  /* Fill the container width */
  height: auto;  /* Maintain aspect ratio */
  display: block;  /* Remove any extra spacing */
  margin: 0 auto;
}

.loading {
  padding: 2rem;
  color: #666;
  font-style: italic;
}

.themes-group {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0;
}

.theme-label {
  font-weight: bold;
}

.theme-options {
  display: flex;
  gap: 1rem;
}

.theme-checkbox {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
}

.theme-checkbox input[type="checkbox"] {
  width: 1.2em;
  height: 1.2em;
  cursor: pointer;
}
</style>
